import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SuccessPopup from '../../components/SuccessPopup';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Divider,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SwapIcon from '@mui/icons-material/SwapCalls';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  fetchParties,
  createSaleInvoice,
  fetchAllProducts,
  getSellerInfo,
  createParty,
  createConsignee,
  fetchConsignees,
  fetchSaleSavedTransporters,
  fetchNextSaleInvoiceNo,
  fetchNextProformaInvoiceNo,
  createProformaInvoice,
  fetchTaxes,
  fetchUnits,
} from '../../api';

export default function Sale() {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const editId = searchParams.get('id');
  const isProforma = searchParams.get('type') === 'pi';

  const [otherCharge, setOtherCharge] = useState(0);
  const [otherDiscount, setOtherDiscount] = useState(0);
  const [otherChargeName, setOtherChargeName] = useState('');
  const [otherDiscountName, setOtherDiscountName] = useState('');
  const [otherDialogOpen, setOtherDialogOpen] = useState(false);
  const [otherType, setOtherType] = useState('charge'); // 'charge' or 'discount'
  const [invoiceNo, setInvoiceNo] = useState('');
  const [autoInvoiceNo, setAutoInvoiceNo] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [invoiceSuffix, setInvoiceSuffix] = useState('');
  const [signStampImage, setSignStampImage] = useState('');
  const [stampUploading, setStampUploading] = useState(false);

  // On mount: if editing, fetch invoice and prefill, else fetch next invoice number
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (editId) {
        // Editing: fetch invoice details
        try {
          const res = await fetch(isProforma ? `/api/proforma-invoices/${editId}` : `/api/sale-invoices/${editId}`);
          const data = await res.json();
          if (!cancelled && data?.data) {
            const { invoice, items, extra, consignee } = data.data;
            setInvoiceNo(invoice.invoice_no || '');
            setAutoInvoiceNo(invoice.invoice_no || '');
            // Split prefix and suffix
            const parts = (invoice.invoice_no || '').split('/');
            const prefix = parts.slice(0, -1).join('/') + '/';
            const suffix = parts[parts.length - 1];
            setInvoicePrefix(prefix);
            setInvoiceSuffix(suffix);
            setSaleDate(toLocalDateStr(isProforma ? invoice.proforma_date : invoice.sale_date));
            setOtherCharge(invoice.other_charge || 0);
            setOtherDiscount(invoice.other_discount || 0);
            setOtherChargeName(invoice.other_charge_name || '');
            setOtherDiscountName(invoice.other_discount_name || '');
            setTermsConditions(invoice.terms_conditions || '');
            setSignStampImage(invoice.sign_stamp_image || '');
            setSelectedBuyer({
              id: invoice.party_id,
              name: invoice.party_name,
              company: invoice.party_company,
              gstin: invoice.party_gstin,
              address: invoice.party_address ?? invoice.address,
              city: invoice.party_city ?? invoice.city,
              state: invoice.party_state ?? invoice.state,
              pincode: invoice.party_pincode ?? invoice.pincode,
            });
            if (consignee) {
              setConsigneeDetails({
                id: consignee.id,
                name: consignee.name,
                company: consignee.company,
                address: consignee.address,
                city: consignee.city,
                state: consignee.state,
                pincode: consignee.pincode,
                gstin: consignee.gstin,
              });
              const consigneeName = String(consignee.name || '');
              const partyName = String(invoice.party_name || '');
              if (consigneeName && consigneeName !== partyName) {
                setBuyerOption('add-consignee');
              }
            }
            setItems(
              (items || []).map((it, idx) => ({
                id: idx + 1,
                itemName: it.item_name,
                productId: it.product_id || null,
                productVariantId: it.product_variant_id || null,
                mrp: it.mrp,
                hsn: it.hsn,
                subtext: it.subtext,
                qty: it.qty,
                unit: it.unit,
                price: it.price,
                taxType: it.tax_type,
                discount: it.discount,
                discountType: it.discount_type,
                taxableValue: it.taxable_value,
                gst: it.gst_percent != null && it.gst_percent !== '' ? String(Number(it.gst_percent)) : '',
                totalAmt: it.total_amt,
              }))
            );
            // Extra details
            if (extra) {
              setTransportDetails((prev) => ({ ...prev, ...extra }));
              setOtherDetails((prev) => ({ ...prev, ...extra }));
            }
            // Restore copy type checkboxes
            setOriginalForRecipient(!!invoice.original_for_recipient);
            setDuplicateForTransporter(!!invoice.duplicate_for_transporter);
            setTriplicateForSupplier(!!invoice.triplicate_for_supplier);
            // Restore invoice type (tax invoice / bill of supply / proforma invoice)
            setFormType(invoice.invoice_type === 'bill-of-supply' ? 'bill-of-supply' : (invoice.invoice_type === 'proforma-invoice' ? 'proforma-invoice' : 'tax-invoice'));
          }
        } catch (e) {
          // ignore
        }
      } else {
        // Creating new: fetch next invoice number
        try {
          const data = isProforma ? await fetchNextProformaInvoiceNo() : await fetchNextSaleInvoiceNo();
          if (!cancelled && data?.nextInvoiceNo) {
            setInvoiceNo(data.nextInvoiceNo);
            setAutoInvoiceNo(data.nextInvoiceNo);
            // Split prefix and suffix
            const parts = data.nextInvoiceNo.split('/');
            const prefix = parts.slice(0, -1).join('/') + '/';
            const suffix = parts[parts.length - 1];
            setInvoicePrefix(prefix);
            setInvoiceSuffix(suffix);
          }
        } catch (e) {
          // ignore
        }
      }
    })();
    return () => { cancelled = true; };
  }, [editId]);
  const [saleDate, setSaleDate] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  });
  const [message, setMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('success');
  const [popupTitle, setPopupTitle] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [createdSale, setCreatedSale] = useState(null);
  const [formType, setFormType] = useState(isProforma ? 'proforma-invoice' : 'tax-invoice');
  const [buyerOption, setBuyerOption] = useState('show-consignee');
  const [originalForRecipient, setOriginalForRecipient] = useState(false);
  const [duplicateForTransporter, setDuplicateForTransporter] = useState(false);
  const [triplicateForSupplier, setTriplicateForSupplier] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyerDialogOpen, setBuyerDialogOpen] = useState(false);
  const [addBuyerDialogOpen, setAddBuyerDialogOpen] = useState(false);
  const [buyerForm, setBuyerForm] = useState({
    name: '', company: '', address: '', city: '', state: '', pincode: '', gstin: '', contact_no: ''
  });
  const [consigneeDialogOpen, setConsigneeDialogOpen] = useState(false);
  const [consigneeSelectDialogOpen, setConsigneeSelectDialogOpen] = useState(false);
  const [consigneeSearch, setConsigneeSearch] = useState('');
  const [consignees, setConsignees] = useState([]);
  const [transporterDialogOpen, setTransporterDialogOpen] = useState(false);
  const [selectTransporterDialogOpen, setSelectTransporterDialogOpen] = useState(false);
  const [savedTransporters, setSavedTransporters] = useState([]);
  const [optionalFieldsExpanded, setOptionalFieldsExpanded] = useState(false);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [consigneeDetails, setConsigneeDetails] = useState(null);
  const [consigneeForm, setConsigneeForm] = useState({ name: '', address: '', city: '', state: '', pincode: '', gstin: '' });
  const [transportDetails, setTransportDetails] = useState({
    transportation_mode: '',
    lr_number: '',
    lr_date: '',
    vehicle_no: '',
    distance_km: '',
    transporter_name: '',
    transporter_address: '',
    transporter_city: '',
    transporter_state: '',
    transporter_pincode: '',
    transporter_gstin: '',
    transporter_contact_no: '',
  });
  const [otherDetails, setOtherDetails] = useState({
    place_of_supply: '',
    date_of_supply: '',
    supply_type: 'B2B',
    challan_number: '',
    ewaybill_number: '',
    sales_person: '',
    due_date: '',
  });
  const [termsConditions, setTermsConditions] = useState(
    '1. This is an electronically generated document.\n2. All disputes are subject to Gurugram jurisdiction.\n3. If the cheque is dishonoured, a penalty of ₹500 + interest @2% per month on the outstanding amount will be charged to the buyer.\n4. Payment shall be made within the credit period agreed; a late payment interest of 2% per month shall apply on overdue amounts.\n5. Goods once sold will not be taken back or exchanged, except for manufacturing defect or damaged/wrong delivery reported within 48 hours of delivery.\n6. The seller reserves the right to withhold delivery of further goods if any previous payment is overdue.\n7. Any shortfall/extra delivery, damages or discrepancies must be reported within 48 hours of delivery.\n8. Title of goods passes to the buyer only after full payment is received by the seller.\n9. Prices are subject to change without prior notice unless confirmed by a written quotation/order.\n10. This invoice is valid only if duly signed and stamped by the authorised signatory.'
  );
  const [items, setItems] = useState([
    { id: 1, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
    { id: 2, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
    { id: 3, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
  ]);

  // Product search state for autocomplete
  const [productOptions, setProductOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [productLoading, setProductLoading] = useState(false);

  // Tax options from settings
  const [taxes, setTaxes] = useState([]);

  // Load taxes from API
  useEffect(() => {
    fetchTaxes()
      .then((res) => {
        const data = res?.data || [];
        setTaxes(data.filter((t) => t.status === 1 || t.status === true));
      })
      .catch(() => setTaxes([]));
  }, []);

  useEffect(() => {
    fetchUnits()
      .then((response) => setUnits(response?.data || []))
      .catch(() => setUnits([]));
  }, []);

  const toNum = useCallback((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const toLocalDateStr = useCallback((val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d)) return typeof val === 'string' ? val.slice(0, 10) : '';
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, []);

  const computeItem = useCallback((item) => {
    const qty = toNum(item.qty);
    const price = toNum(item.price);
    const base = qty * price;
    const discountVal = item.discountType === '₹' ? toNum(item.discount) : (base * toNum(item.discount)) / 100;
    const gstPercent = toNum(item.gst);
    
    // Handle Inclusive vs Exclusive tax type
    if (item.taxType === 'Inclusive') {
      // Price includes GST - deduct GST from the price
      const priceAfterDiscount = Math.max(0, base - discountVal);
      const taxable = priceAfterDiscount / (1 + gstPercent / 100);
      return {
        taxableValue: taxable.toFixed(2),
        totalAmt: priceAfterDiscount.toFixed(2),
      };
    } else {
      // Price excludes GST - add GST on top (Exclusive)
      const taxable = Math.max(0, base - discountVal);
      const total = taxable + (taxable * gstPercent) / 100;
      return {
        taxableValue: taxable.toFixed(2),
        totalAmt: total.toFixed(2),
      };
    }
  }, [toNum]);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = prev.map((it) => ({ ...it }));
      next[index][field] = value;
      if (field === 'itemName') {
        next[index].productId = null;
        next[index].productVariantId = null;
      }
      const computed = computeItem(next[index]);
      next[index].taxableValue = computed.taxableValue;
      next[index].totalAmt = computed.totalAmt;
      return next;
    });
  };

  const handleDeleteItem = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleAddItem = () => {
    const newItem = {
      id: items.length + 1,
      itemName: '',
      mrp: '',
      hsn: '',
      subtext: '',
      qty: '',
      unit: '',
      price: '',
      taxType: 'Exclusive',
      discount: '',
      discountType: '%',
      taxableValue: '',
      gst: '',
      totalAmt: ''
    };
    setItems([...items, newItem]);
  };

  // Search products for autocomplete
  const handleProductSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setProductOptions([]);
      return;
    }
    
    setProductLoading(true);
    try {
      const res = await fetchAllProducts(searchQuery.trim());
      if (res?.ok && res?.data) {
        // Flatten products with their variants into selectable options
        const options = [];
        res.data.forEach(product => {
          if (product.variants && product.variants.length > 0) {
            product.variants.forEach(variant => {
              options.push({
                label: !variant.variant_name || variant.variant_name === 'Default'
                  ? product.name
                  : `${product.name} - ${variant.variant_name}${variant.unit_name ? ` (${variant.unit_name})` : ''}`,
                product,
                variant,
              });
            });
          } else {
            // Product without variants
            options.push({
              label: product.name,
              product,
              variant: null,
            });
          }
        });
        setProductOptions(options);
      } else {
        setProductOptions([]);
      }
    } catch (err) {
      console.error('Product search error:', err);
      setProductOptions([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  // Load consignees when opening the select dialog or when search changes
  const openConsigneeSelect = useCallback(async () => {
    setConsigneeSelectDialogOpen(true);
    setConsigneeSearch('');
    try {
      const data = await fetchConsignees('');
      setConsignees(data?.items || []);
    } catch (e) {
      console.error('fetch consignees error', e);
      setConsignees([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!consigneeSelectDialogOpen) return;
      try {
        const data = await fetchConsignees(consigneeSearch);
        if (!cancelled) setConsignees(data?.items || []);
      } catch (e) {
        if (!cancelled) setConsignees([]);
      }
    })();
    return () => { cancelled = true; };
  }, [consigneeSelectDialogOpen, consigneeSearch]);

  // Handle product selection from autocomplete
  const handleProductSelect = useCallback((index, option) => {
    if (!option) return;
    
    const { product, variant } = option;
    if (variant && Number(product.is_unlimited_stock) !== 1 && Number(variant.stock) <= 0) {
      setPopupType('error');
      setPopupTitle('Out of Stock');
      setPopupMessage(`${product.name || 'This product'} is not in stock.`);
      setShowPopup(true);
      return;
    }
    
    setItems((prev) => {
      const next = prev.map((it) => ({ ...it }));
      const item = next[index];
      
      // Auto-fill fields from product/variant
      item.itemName = product.name || '';
      item.productId = product.id || null;
      item.productVariantId = variant?.id || null;
      // price column = MRP, discounted_price column = sale price
      item.mrp = variant?.price || product.price || '';
      item.price = variant?.discounted_price || variant?.price || product.price || '';
      item.hsn = variant?.hsn || product.hsn || '';
      item.unit = variant?.unit_name || '';
      
      // Keep existing values for other fields or set defaults
      if (!item.qty) item.qty = '1';
      
      // Set GST from product's configured tax percentage if available
      // Product has tax_id and tax_percentage from the database
      if (!item.gst && product.tax_percentage !== undefined) {
        item.gst = String(product.tax_percentage);
      } else if (!item.gst) {
        // Default to 18 if no product tax config found
        item.gst = '18';
      }
      
      // Recompute totals
      const computed = computeItem(item);
      item.taxableValue = computed.taxableValue;
      item.totalAmt = computed.totalAmt;
      
      return next;
    });
  }, [computeItem]);

  const totals = useMemo(() => {
    const base = items.reduce(
      (acc, item) => {
        const { taxableValue, totalAmt } = computeItem(item);
        const taxable = toNum(taxableValue);
        const total = toNum(totalAmt);
        const gst = total - taxable;
        return {
          taxable: acc.taxable + taxable,
          gst: acc.gst + gst,
          total: acc.total + total,
        };
      },
      { taxable: 0, gst: 0, total: 0 }
    );
    // Calculate final amount with otherCharge and otherDiscount
    const final = base.total + (Number(otherCharge) || 0) - (Number(otherDiscount) || 0);
    return { ...base, final };
  }, [items, computeItem, toNum, otherCharge, otherDiscount]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const term = customerSearch.toLowerCase();
    return customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term) ||
        c.gstin?.toLowerCase().includes(term)
    );
  }, [customers, customerSearch]);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const res = await fetchParties('customer');
        setCustomers(res.data || []);
      } catch (err) {
        console.error('fetch customers error', err);
      }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    const loadSeller = async () => {
      try {
        const res = await getSellerInfo();
        setSellerInfo(res?.data || null);
      } catch (err) {
        console.error('fetch seller error', err);
      }
    };
    loadSeller();
  }, []);

  // Prefill transporter details from seller (our own org) when available
  useEffect(() => {
    if (!sellerInfo) return;
    setTransportDetails((prev) => ({
      ...prev,
      transporter_name: prev.transporter_name || sellerInfo?.store_name || sellerInfo?.company || '',
      transporter_address: prev.transporter_address || sellerInfo?.street || '',
      transporter_city: prev.transporter_city || sellerInfo?.city_name || sellerInfo?.city || '',
      transporter_state: prev.transporter_state || sellerInfo?.state || '',
      transporter_pincode: prev.transporter_pincode || sellerInfo?.pincode_value || sellerInfo?.pincode || '',
      transporter_gstin: prev.transporter_gstin || sellerInfo?.tax_number || '',
      transporter_contact_no:
        prev.transporter_contact_no || sellerInfo?.contact_no || sellerInfo?.phone || sellerInfo?.mobile || '',
    }));
  }, [sellerInfo]);

  // Load saved transporters when select dialog opens
  useEffect(() => {
    const loadSavedTransporters = async () => {
      if (!selectTransporterDialogOpen) return;
      try {
        const data = await fetchSaleSavedTransporters();
        setSavedTransporters(data?.items || []);
      } catch (err) {
        console.error('fetch saved transporters error', err);
        setSavedTransporters([]);
      }
    };
    loadSavedTransporters();
  }, [selectTransporterDialogOpen]);

  const handleSelectTransporter = (transporter) => {
    setTransportDetails((prev) => ({
      ...prev,
      transporter_name: transporter.transporter_name || '',
      transporter_address: transporter.transporter_address || '',
      transporter_city: transporter.transporter_city || '',
      transporter_state: transporter.transporter_state || '',
      transporter_pincode: transporter.transporter_pincode || '',
      transporter_gstin: transporter.transporter_gstin || '',
      transporter_contact_no: transporter.transporter_contact_no || '',
    }));
    setSelectTransporterDialogOpen(false);
  };

  const handleStampUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setMessage('Upload a PNG, JPEG, or WebP image up to 2 MB.');
      return;
    }

    setStampUploading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('stamp', file);
      const response = await fetch('/api/uploads/stamp', { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Stamp upload failed');
      setSignStampImage(result.url);
    } catch (error) {
      setMessage(error.message || 'Stamp upload failed');
    } finally {
      setStampUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!invoiceNo.trim()) {
      setMessage('Invoice number is required');
      return;
    }
    if (!selectedBuyer?.id) {
      setMessage('Select a customer');
      return;
    }
    const hasTaxApplied = items.some((it) => it.itemName && String(it.itemName).trim() && toNum(it.gst) > 0);
    const invoiceType = formType === 'bill-of-supply' ? 'bill-of-supply' : (formType === 'proforma-invoice' ? 'proforma-invoice' : (hasTaxApplied ? 'tax-invoice' : 'general-invoice'));
    const payload = {
      invoice_no: invoiceNo.trim(),
      invoice_type: invoiceType,
      party_id: selectedBuyer.id,
      ...(formType === 'proforma-invoice' ? { proforma_date: saleDate || null } : { sale_date: saleDate || null }),
      sign_stamp_image: signStampImage || null,
      taxable_amount: Number(totals.taxable.toFixed(2)),
      gst_amount: Number(totals.gst.toFixed(2)),
      total_amount: Number(totals.total.toFixed(2)),
      final_amount: Number(totals.final.toFixed(2)),
      other_charge: Number(otherCharge) || 0,
      other_discount: Number(otherDiscount) || 0,
      other_charge_name: otherChargeName,
      other_discount_name: otherDiscountName,
      original_for_recipient: originalForRecipient,
      duplicate_for_transporter: duplicateForTransporter,
      triplicate_for_supplier: triplicateForSupplier,
      consignee_id: consigneeDetails?.id || null,
      items: items
        .filter((it) => it.itemName && String(it.itemName).trim())
        .map((it) => ({
          item_name: it.itemName,
          product_id: it.productId || null,
          product_variant_id: it.productVariantId || null,
          mrp: toNum(it.mrp) || null,
          hsn: it.hsn || null,
          subtext: it.subtext || null,
          qty: toNum(it.qty) || null,
          unit: it.unit || null,
          price: toNum(it.price) || null,
          tax_type: it.taxType || 'Exclusive',
          discount: toNum(it.discount) || null,
          discount_type: it.discountType || '%',
          taxable_value: toNum(it.taxableValue) || null,
          gst_percent: toNum(it.gst) || null,
          total_amt: toNum(it.totalAmt) || null,
        })),
      extra: {
        ...transportDetails,
        ...otherDetails,
        terms_conditions: termsConditions,
        consignee:
          buyerOption === 'add-consignee'
            ? consigneeDetails
            : buyerOption === 'show-consignee'
            ? {
                id: selectedBuyer?.id,
                name: selectedBuyer?.name || selectedBuyer?.company || '',
                address: selectedBuyer?.address || '',
                city: selectedBuyer?.city || '',
                state: selectedBuyer?.state || '',
                pincode: selectedBuyer?.pincode || '',
                gstin: selectedBuyer?.gstin || '',
              }
            : null,
      },
    };

    setLoading(true);
    setMessage('');
    setCreatedSale(null);
    try {
      if (editId) {
        // Update existing invoice
        const url = formType === 'proforma-invoice' ? `/api/proforma-invoices/${editId}` : `/api/sale-invoices/${editId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Save failed');
        setPopupType('success');
        setPopupTitle('Success');
        setPopupMessage(formType === 'proforma-invoice' ? 'Proforma invoice updated successfully' : 'Invoice updated successfully');
        setShowPopup(true);
      } else {
        const created = formType === 'proforma-invoice' ? await createProformaInvoice(payload) : await createSaleInvoice(payload);
        setCreatedSale(created);
        // Reset all form fields
        setInvoiceNo(autoInvoiceNo); // fetchNextSaleInvoiceNo will run on mount, but set again for clarity
        setSaleDate((() => {
          const d = new Date();
          return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        })());
        setSelectedBuyer(null);
        setBuyerOption('show-consignee');
        setConsigneeDetails(null);
        setConsigneeForm({ name: '', address: '', city: '', state: '', pincode: '', gstin: '' });
        setTransportDetails({
          transportation_mode: '',
          lr_number: '',
          lr_date: '',
          vehicle_no: '',
          distance_km: '',
          transporter_name: '',
          transporter_address: '',
          transporter_city: '',
          transporter_state: '',
          transporter_pincode: '',
          transporter_gstin: '',
          transporter_contact_no: '',
        });
        setOtherDetails({
          place_of_supply: '',
          date_of_supply: '',
          supply_type: 'B2B',
          challan_number: '',
          ewaybill_number: '',
          sales_person: '',
          due_date: '',
        });
        setTermsConditions('1. This is an electronically generated document.\n2. All disputes are subject to Gurugram jurisdiction.\n3. If the cheque is dishonoured, a penalty of ₹500 + interest @2% per month on the outstanding amount will be charged to the buyer.\n4. Payment shall be made within the credit period agreed; a late payment interest of 2% per month shall apply on overdue amounts.\n5. Goods once sold will not be taken back or exchanged, except for manufacturing defect or damaged/wrong delivery reported within 48 hours of delivery.\n6. The seller reserves the right to withhold delivery of further goods if any previous payment is overdue.\n7. Any shortfall/extra delivery, damages or discrepancies must be reported within 48 hours of delivery.\n8. Title of goods passes to the buyer only after full payment is received by the seller.\n9. Prices are subject to change without prior notice unless confirmed by a written quotation/order.\n10. This invoice is valid only if duly signed and stamped by the authorised signatory.');
        setSignStampImage('');
        setItems([
          { id: 1, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
          { id: 2, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
          { id: 3, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
        ]);
        setOtherCharge(0);
        setOtherDiscount(0);
        setOtherChargeName('');
        setOtherDiscountName('');
        setPopupType('success');
        setPopupTitle('Success');
        setPopupMessage(formType === 'proforma-invoice' ? 'Proforma invoice created successfully' : 'Invoice created successfully');
        setShowPopup(true);
      }
    } catch (err) {
      setPopupType('error');
      setPopupTitle('Error');
      setPopupMessage(err?.response?.data?.error || err.message || 'Save failed');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1, bgcolor: '#f5f5f5', minHeight: '100vh', width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <IconButton onClick={() => navigate(isProforma ? '/accounts/proforma-invoices' : '/accounts/invoices?tab=1')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isProforma ? 'Proforma Invoice' : 'Create Sale'}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" size="small" onClick={() => navigate(isProforma ? '/accounts/proforma-invoices' : '/accounts/invoices?tab=1')}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" size="small" disabled={loading} onClick={handleSubmit}>
            {loading ? 'Saving...' : 'Submit'}
          </Button>
        </Box>
      </Box>

      {message && (
        <Typography variant="caption" sx={{ color: '#2e7d32', ml: 1, mb: 1, display: 'block' }}>
          {message}
        </Typography>
      )}

      <SuccessPopup
        open={showPopup}
        setOpen={setShowPopup}
        type={popupType}
        title={popupTitle}
        message={popupMessage}
        onCreateNew={() => { window.location.href = formType === 'proforma-invoice' ? '/accounts/sale?type=pi' : '/accounts/sale'; }}
        onPrint={() => { const printId = editId || createdSale?.id; if (printId) window.open(formType === 'proforma-invoice' ? `/print-invoice/${printId}?type=pi` : `/print-invoice/${printId}`, '_blank'); }}
        onSeeInvoices={() => navigate('/accounts/Invoices')}
        isPurchase={false}
      />

      <Grid
        container
        spacing={1}
        sx={{ flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'stretch', pb: 1 }}
      >
        {/* Invoice Details Column */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper sx={{ p: 1.5, height: '100%', width: '100%', minWidth: 0, bgcolor: '#e0f2f1', borderTop: '4px solid #00897b' }}>
            {/* Form Type Selection */}
            <Box sx={{ mb: 1.5 }}>
              <RadioGroup
                row
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
                sx={{ gap: 0.5 }}
              >
                {isProforma ? (
                  <FormControlLabel
                    value="proforma-invoice"
                    control={<Radio size="small" />}
                    label={<Typography variant="caption">Proforma Invoice (PI)</Typography>}
                  />
                ) : (
                  <>
                    <FormControlLabel
                      value="tax-invoice"
                      control={<Radio size="small" />}
                      label={<Typography variant="caption">Tax Invoice</Typography>}
                    />
                    <FormControlLabel
                      value="bill-of-supply"
                      control={<Radio size="small" />}
                      label={<Typography variant="caption">Bill of Supply</Typography>}
                    />
                  </>
                )}
              </RadioGroup>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Grid container spacing={1}>
              {/* Sale Date */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography variant="caption" sx={{ color: '#666', flex: '0 0 62px' }}>
                  Sale Date
                </Typography>
                <TextField
                  type="date"
                  value={saleDate}
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setSaleDate(e.target.value)}
                  inputProps={{ style: { fontSize: '0.85rem', height: 24, padding: '4px 8px' } }}
                  sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.85rem', height: 24 } }}
                />
                </Box>
              </Grid>

              {/* Invoice Sale No. */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography variant="caption" sx={{ color: '#666', flex: '0 0 116px' }}>
                  Sales Invoice No.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <Typography sx={{ mr: 1, fontWeight: 500, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{invoicePrefix}</Typography>
                  <TextField
                    value={invoiceSuffix}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setInvoiceSuffix(val);
                      setInvoiceNo(invoicePrefix + val);
                    }}
                    size="small"
                    variant="outlined"
                    inputProps={{ maxLength: 6, style: { textAlign: 'center', width: 40, fontSize: '0.85rem', padding: 2 } }}
                    sx={{ flex: 1, '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.85rem', height: 24 } }}
                  />
                  {autoInvoiceNo && invoiceNo !== autoInvoiceNo && (
                    <Button size="small" color="secondary" sx={{ minWidth: 0, fontSize: 10, ml: 1, height: 24, lineHeight: 1 }} onClick={() => {
                      const parts = autoInvoiceNo.split('/');
                      const prefix = parts.slice(0, -1).join('/') + '/';
                      const suffix = parts[parts.length - 1];
                      setInvoicePrefix(prefix);
                      setInvoiceSuffix(suffix);
                      setInvoiceNo(autoInvoiceNo);
                    }}>
                      Reset
                    </Button>
                  )}
                </Box>
                </Box>
                {/* Optionally show helper text */}
                {autoInvoiceNo && invoiceNo !== autoInvoiceNo && (
                  <Typography variant="caption" color="secondary" sx={{ display: 'block', ml: '124px' }}>Auto: {autoInvoiceNo}</Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Seller Details Column ({sellerInfo?.store_name || 'SELLER NAME'}) */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper
            sx={{
              p: 1.5,
              height: '100%',
              width: '100%',
              minWidth: 0,
              bgcolor: '#f3e5f5',
              borderTop: '4px solid #7b1fa2',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                SELLER DETAILS
              </Typography>
              <IconButton size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {sellerInfo?.store_name || 'STORE NAME'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
              {sellerInfo?.formatted_address || sellerInfo?.street || 'Street address'}
            </Typography>
            {sellerInfo?.formatted_address ? (
              <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                GSTIN: {sellerInfo?.tax_number || '-'}
              </Typography>
            ) : (
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    City:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    {sellerInfo?.city_name || 'City'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    State:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    {sellerInfo?.state || 'State'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Pincode:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    {sellerInfo?.pincode_value || '000000'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    GSTIN:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                    {sellerInfo?.tax_number || '-'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Buyer Details Column (Customer) */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper sx={{ p: 1.5, height: '100%', width: '100%', minWidth: 0, bgcolor: '#e8eaf6', borderTop: '4px solid #5c6bc0' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                BUYER DETAILS
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" color="primary" onClick={() => setBuyerDialogOpen(true)} title="Select Buyer">
                  <PersonSearchIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="primary" title="Add New Buyer" onClick={() => setAddBuyerDialogOpen(true)}>
                  <PersonAddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {selectedBuyer?.name || 'CUSTOMER NAME'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
              {selectedBuyer?.address || 'Address: Please select a buyer'}
            </Typography>

            <Grid container spacing={0.5}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  City:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {selectedBuyer?.city || 'City Name'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  State:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {selectedBuyer?.state || 'State Name'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Pincode:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {selectedBuyer?.pincode || '000000'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  GSTIN:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                  {selectedBuyer?.gstin || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Consignee Details Column */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper sx={{ p: 1.5, height: '100%', width: '100%', minWidth: 0, bgcolor: '#fff3e0', borderTop: '4px solid #f57c00' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                CONSIGNEE DETAILS
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" color="primary" title="Select Consignee" onClick={openConsigneeSelect}>
                  <SearchIcon fontSize="small" />
                </IconButton>
                <Select
                  value={buyerOption}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBuyerOption(value);
                    if (value === 'add-consignee') {
                      setConsigneeDialogOpen(true);
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 140, height: 28, fontSize: '0.75rem' }}
                >
                  <MenuItem value="show-consignee">Same as Buyer</MenuItem>
                  <MenuItem value="same-as-seller">Same as Seller</MenuItem>
                  <MenuItem value="not-required">Not Required</MenuItem>
                  <MenuItem value="add-consignee">Different Address</MenuItem>
                </Select>
              </Box>
            </Box>

            {buyerOption === 'show-consignee' && selectedBuyer && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {selectedBuyer?.name || selectedBuyer?.company}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  {selectedBuyer?.address || 'Buyer Address Details'}
                </Typography>

                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      City:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {selectedBuyer?.city || 'City Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      State:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {selectedBuyer?.state || 'State Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Pincode:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {selectedBuyer?.pincode || '000000'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      GSTIN:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                      {selectedBuyer?.gstin || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}

            {buyerOption === 'same-as-seller' && sellerInfo && (
              <>
                <Divider sx={{ my: 1 }} />
<Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {sellerInfo?.store_name || 'STORE NAME'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  {sellerInfo?.formatted_address || sellerInfo?.street || 'Street address'}
                </Typography>

                {sellerInfo?.formatted_address ? (
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    GSTIN: {sellerInfo?.tax_number || '-'}
                  </Typography>
                ) : (
                  <Grid container spacing={0.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        City:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                        {sellerInfo?.city_name || 'City'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        State:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                        {sellerInfo?.state || 'State'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Pincode:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                        {sellerInfo?.pincode_value || '000000'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        GSTIN:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                        {sellerInfo?.tax_number || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {buyerOption === 'add-consignee' && consigneeDetails && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {consigneeDetails?.name || 'CONSIGNEE NAME'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  {consigneeDetails?.address || 'Consignee Address Details'}
                </Typography>

                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      City:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {consigneeDetails?.city || 'City Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      State:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {consigneeDetails?.state || 'State Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Pincode:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {consigneeDetails?.pincode || '000000'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      GSTIN:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                      {consigneeDetails?.gstin || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Products */}
      <Paper sx={{ p: 1.5, mt: 2, bgcolor: '#e8e8e8' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ fontSize: '1.2rem' }}>📦</Box>
            ITEM DETAILS
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ height: 30 }} onClick={handleAddItem}>
            Add New Item
          </Button>
        </Box>
        
        <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ width: 40, p: 0.5 }}>#</TableCell>
                <TableCell sx={{ minWidth: 180, p: 0.5 }}>Item Name</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>Mrp</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>HSN</TableCell>
                <TableCell sx={{ width: 60, p: 0.5 }}>Qty</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>Unit</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>Price</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Tax Type</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Discount</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Taxable Value</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>GST</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Total Amt</TableCell>
                <TableCell sx={{ width: 40, p: 0.5 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ p: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DragIndicatorIcon fontSize="small" sx={{ color: '#999', cursor: 'move' }} />
                      {index + 1}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      size="small"
                      options={productOptions}
                      loading={productLoading}
                      value={item.itemName}
                      onInputChange={(event, newValue) => {
                        handleItemChange(index, 'itemName', newValue);
                        handleProductSearch(newValue);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'object' && newValue !== null) {
                          handleProductSelect(index, newValue);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.defaultMuiPrevented) {
                          const typed = String(item.itemName || '').trim().toLowerCase();
                          if (typed && productOptions.length) {
                            const exact = productOptions.find((opt) => opt.label.toLowerCase() === typed);
                            handleProductSelect(index, exact || productOptions[0]);
                          }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search or Enter Item Name"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {productLoading ? <Typography variant="caption" sx={{ mr: 1 }}>...</Typography> : null}
                                {params.InputProps.endAdornment}
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                                </IconButton>
                              </>
                            ),
                            sx: { height: 30 }
                          }}
                        />
                      )}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={item.subtext}
                      placeholder="Subtext (e.g. Mfg/Expiry details)"
                      InputProps={{ sx: { height: 24, fontSize: '0.7rem', mt: 0.5 } }}
                      onChange={(e) => handleItemChange(index, 'subtext', e.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.mrp} placeholder="-" InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'mrp', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.hsn} placeholder="-" InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'hsn', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.qty} placeholder="-" InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select fullWidth size="small" displayEmpty value={item.unit || ''} sx={{ height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'unit', e.target.value)}>
                      <MenuItem value=""><em>Select unit</em></MenuItem>
                      {units.map((unit) => <MenuItem key={unit.id} value={unit.name}>{unit.name}{unit.short_code ? ` (${unit.short_code})` : ''}</MenuItem>)}
                      {item.unit && !units.some((u) => u.name === item.unit) && (
                        <MenuItem value={item.unit}>{item.unit}</MenuItem>
                      )}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.price} placeholder="₹ -" InputProps={{ startAdornment: <Typography variant="caption">₹</Typography>, sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'price', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select fullWidth size="small" value={item.taxType} sx={{ height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'taxType', e.target.value)}>
                      <MenuItem value="Exclusive">Exclusive</MenuItem>
                      <MenuItem value="Inclusive">Inclusive</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <TextField fullWidth size="small" value={item.discount} placeholder="-" sx={{ flex: 1, minWidth: 50 }} InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'discount', e.target.value)} />
                      <Select size="small" value={item.discountType} sx={{ width: 50, height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'discountType', e.target.value)}>
                        <MenuItem value="%">%</MenuItem>
                        <MenuItem value="₹">₹</MenuItem>
                      </Select>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.taxableValue} placeholder="-" readOnly InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select fullWidth size="small" value={item.gst} displayEmpty sx={{ height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'gst', e.target.value)}>
                      <MenuItem value="">No Tax</MenuItem>
                      {taxes.map((tax) => (
                        <MenuItem key={tax.id} value={String(tax.percentage)}>
                          {tax.title} ({tax.percentage}%)
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.totalAmt} placeholder="-" readOnly InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Button 
          fullWidth 
          size="small" 
          variant="outlined" 
          startIcon={<AddIcon />}
          sx={{ mt: 1, borderStyle: 'dashed', height: 30 }}
          onClick={handleAddItem}
        >
          Add More Item
        </Button>

        {/* Summary Section */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 300 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Taxable Amount</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>₹ {totals.taxable.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">GST Amount</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>₹ {totals.gst.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Total Amount</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>₹ {totals.total.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
            {/* Show Other Charge/Discount Name and Amount if present */}
            {otherCharge > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">
                    {otherChargeName ? otherChargeName : 'Other Charge'}
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.1, color: 'error.main', ml: 0 }} onClick={() => { setOtherCharge(0); setOtherChargeName(''); }}>
                    <CloseIcon fontSize="inherit" style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>+ ₹ {Number(otherCharge).toFixed(2)}</Typography>
              </Box>
            )}
            {otherDiscount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">
                    {otherDiscountName ? otherDiscountName : 'Other Discount'}
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.1, color: 'error.main', ml: 0 }} onClick={() => { setOtherDiscount(0); setOtherDiscountName(''); }}>
                    <CloseIcon fontSize="inherit" style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>- ₹ {Number(otherDiscount).toFixed(2)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Final Amount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>₹ {totals.final.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ height: 30, whiteSpace: 'nowrap', flex: 1 }}
                onClick={() => { setOtherType('charge'); setOtherDialogOpen(true); }}>
                Add Other Charge
              </Button>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ height: 30, whiteSpace: 'nowrap', flex: 1 }}
                onClick={() => { setOtherType('discount'); setOtherDialogOpen(true); }}>
                Add Other Discount
              </Button>
              {/* Other Charge/Discount Dialog */}
              <Dialog open={otherDialogOpen} onClose={() => setOtherDialogOpen(false)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {otherType === 'charge' ? 'Add Other Charge' : 'Add Other Discount'}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label={otherType === 'charge' ? 'Other Charge Amount' : 'Other Discount Amount'}
                    value={otherType === 'charge' ? otherCharge : otherDiscount}
                    onChange={e => {
                      const val = e.target.value;
                      if (otherType === 'charge') setOtherCharge(val);
                      else setOtherDiscount(val);
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label={otherType === 'charge' ? 'Other Charge Name/Description' : 'Other Discount Name/Description'}
                    value={otherType === 'charge' ? otherChargeName : otherDiscountName}
                    onChange={e => {
                      if (otherType === 'charge') setOtherChargeName(e.target.value);
                      else setOtherDiscountName(e.target.value);
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" size="small" onClick={() => setOtherDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained" size="small" onClick={() => setOtherDialogOpen(false)}>
                      Save
                    </Button>
                  </Box>
                </Box>
              </Dialog>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Optional Fields */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{
            bgcolor: '#ccc',
            p: 1,
            borderRadius: 1,
            fontWeight: 600,
            mb: 1,
            display: 'block',
          }}
        >
          Optional Fields
        </Typography>

        {/* Transportation Details and Other Details Side by Side */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 2 }}>
          {/* Transportation Details */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Accordion 
              expanded={optionalFieldsExpanded} 
              onChange={() => setOptionalFieldsExpanded(!optionalFieldsExpanded)}
              sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  🚚 TRANSPORTATION DETAILS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                <Box sx={{ width: '100%' }}>
                  {/* Transportation Mode */}
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    Transportation Mode
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {['Road', 'Rail', 'Air', 'Ship/Road Cum Ship', 'Not Applicable'].map((mode) => (
                      <Button
                        key={mode}
                        size="small"
                        variant={transportDetails.transportation_mode === mode ? 'contained' : 'outlined'}
                        sx={{ height: 28, fontSize: '0.7rem' }}
                        onClick={() => setTransportDetails((prev) => ({ ...prev, transportation_mode: mode }))}
                      >
                        {mode}
                      </Button>
                    ))}
                  </Box>

                  {/* LR Number and LR Date */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        LR Number
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter LR Number"
                        size="small"
                        variant="outlined"
                        value={transportDetails.lr_number}
                        onChange={(e) => setTransportDetails((prev) => ({ ...prev, lr_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        LR Date
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={transportDetails.lr_date}
                        onChange={(e) => setTransportDetails((prev) => ({ ...prev, lr_date: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  {/* Vehicle No. and Approximate Distance */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Vehicle No.
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Vehicle No."
                        size="small"
                        variant="outlined"
                        value={transportDetails.vehicle_no}
                        onChange={(e) => setTransportDetails((prev) => ({ ...prev, vehicle_no: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Approximate Distance (in km)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={transportDetails.distance_km}
                          onChange={(e) => setTransportDetails((prev) => ({ ...prev, distance_km: e.target.value }))}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Transporter Details */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#666' }}>
                      Transporter Details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => setSelectTransporterDialogOpen(true)} sx={{ fontSize: '0.7rem', color: '#5c6bc0', p: 0.5, minWidth: 'auto' }} title="Select saved transporter">
                        <SwapIcon fontSize="small" />
                      </IconButton>
                      <Button size="small" variant="text" onClick={() => setTransporterDialogOpen(true)} sx={{ fontSize: '0.7rem', color: '#5c6bc0', p: 0, minWidth: 'auto' }}>
                        + Add New Transporter
                      </Button>
                    </Box>
                  </Box>
                  {(transportDetails.transporter_name || transportDetails.transporter_address) && (
                    <Box sx={{ bgcolor: '#f9f9f9', p: 1, borderRadius: 0.5, mb: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.2 }}>
                        {transportDetails.transporter_name || '-'}{transportDetails.transporter_contact_no ? `, ${transportDetails.transporter_contact_no}` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {[transportDetails.transporter_address, transportDetails.transporter_city, transportDetails.transporter_state, transportDetails.transporter_pincode].filter(Boolean).join(', ') || '-'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Other Details */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Accordion 
              expanded={optionalFieldsExpanded} 
              onChange={() => setOptionalFieldsExpanded(!optionalFieldsExpanded)}
              sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  📋 OTHER DETAILS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                <Box sx={{ width: '100%' }}>
                  {/* Order Details: Place of Supply, Date of Supply, Supply Type */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Place of Supply
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Place of Supply"
                        size="small"
                        variant="outlined"
                        value={otherDetails.place_of_supply}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, place_of_supply: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Date of Supply
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={otherDetails.date_of_supply}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, date_of_supply: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Supply Type
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={otherDetails.supply_type}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, supply_type: e.target.value }))}
                      >
                        <MenuItem value="B2B">B2B</MenuItem>
                        <MenuItem value="B2C">B2C</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>

                  {/* Challan Number and E-Waybill Number */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Challan Number
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Challan Number"
                        size="small"
                        variant="outlined"
                        value={otherDetails.challan_number}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, challan_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        E-Waybill Number
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter E-Waybill Number"
                        size="small"
                        variant="outlined"
                        value={otherDetails.ewaybill_number}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, ewaybill_number: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  {/* Sales Person and Due Date */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Sales Person
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Sales Person Name"
                        size="small"
                        variant="outlined"
                        value={otherDetails.sales_person}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, sales_person: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Due Date
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={otherDetails.due_date}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, due_date: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>

        {/* Document Copies */}
        {formType !== 'proforma-invoice' && (
        <Paper sx={{ p: 2, mb: 1, bgcolor: '#f0f7ff', border: '2px solid #5c6bc0', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={originalForRecipient}
                  onChange={(e) =>
                    setOriginalForRecipient(e.target.checked)
                  }
                />
              }
              label={<Typography variant="caption">Original for Recipient</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={duplicateForTransporter}
                  onChange={(e) =>
                    setDuplicateForTransporter(e.target.checked)
                  }
                />
              }
              label={<Typography variant="caption">Duplicate for Transporter</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={triplicateForSupplier}
                  onChange={(e) =>
                    setTriplicateForSupplier(e.target.checked)
                  }
                />
              }
              label={<Typography variant="caption">Triplicate for Supplier</Typography>}
            />
          </Box>
        </Paper>
        )}

        {/* Bank Details, Terms & Conditions, Upload Signature - Side by Side */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 2 }}>
          {/* Bank Details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  🏦 BANK / PAYMENT DETAILS
                </Typography>
                </Box>

              <Divider sx={{ mb: 1 }} />

              {/* Bank Logo Placeholder */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ width: 60, height: 30, bgcolor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, mr: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>BANK</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sellerInfo?.bank_name || 'BANK NAME'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>A/C Holder Name</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{sellerInfo?.account_name || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>A/C No.</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{sellerInfo?.account_number || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>IFSC Code</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{sellerInfo?.bank_ifsc_code || '-'}</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Terms & Conditions */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  📋 TERMS AND CONDITIONS
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 1 }} />

              <TextField
                fullWidth
                multiline
                minRows={4}
                size="small"
                value={termsConditions}
                onChange={(e) => setTermsConditions(e.target.value)}
                placeholder="Enter terms and conditions"
              />
            </Paper>
          </Box>

          {/* Upload Signature */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5f5' }}>
              <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', mb: 1, fontSize: '0.7rem', color: '#666' }}>
                Certified that the particular given above are true and correct,
              </Typography>
              <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', mb: 1.5, fontSize: '0.75rem', fontWeight: 600 }}>
                For, {sellerInfo?.store_name || 'SELLER NAME'}
              </Typography>
              
              {/* Signature Box */}
              <Box sx={{ 
                border: '2px dashed #ccc', 
                borderRadius: 1, 
                minHeight: 100, 
                mb: 1,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                p: 1,
                bgcolor: '#fff'
              }}>
                {signStampImage ? (
                  <Box component="img" src={signStampImage} alt="Uploaded stamp" sx={{ maxWidth: '100%', maxHeight: 90, objectFit: 'contain' }} />
                ) : (
                  <Typography variant="caption" sx={{ color: '#888' }}>No stamp uploaded</Typography>
                )}
              </Box>
              
              <Button 
                component="label"
                size="small" 
                variant="text" 
                fullWidth 
                disabled={stampUploading}
                sx={{ fontSize: '0.7rem', color: '#5c6bc0', mb: 1 }}
              >
                {stampUploading ? 'Uploading...' : 'Upload Stamp'}
                <input hidden accept="image/jpeg,image/png,image/webp" type="file" onChange={handleStampUpload} />
              </Button>
              {signStampImage && (
                <Button size="small" variant="text" startIcon={<DeleteIcon />} onClick={() => setSignStampImage('')} sx={{ display: 'block', mx: 'auto', mb: 1, fontSize: '0.7rem' }}>
                  Clear Stamp
                </Button>
              )}
              
              <Divider sx={{ mb: 0.5 }} />
              
              <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', fontSize: '0.7rem', color: '#666' }}>
                Authorised Signatory
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Buyer Selection Dialog */}
      <Dialog
        open={buyerDialogOpen}
        onClose={() => setBuyerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Buyer
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, company, GSTIN"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List>
            {filteredCustomers.map((buyer) => (
              <ListItemButton
                key={buyer.id}
                onClick={() => {
                  setSelectedBuyer(buyer);
                  setBuyerDialogOpen(false);
                }}
              >
                <ListItemText
                  primary={buyer.name || buyer.company}
                  secondary={`${buyer.city || ''} ${buyer.city && buyer.gstin ? '-' : ''} ${buyer.gstin || ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Dialog>

      {/* Consignee Selection Dialog */}
      <Dialog
        open={consigneeSelectDialogOpen}
        onClose={() => setConsigneeSelectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Consignee
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, company, GSTIN"
            value={consigneeSearch}
            onChange={(e) => setConsigneeSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List>
            {(consignees || []).map((c) => (
              <ListItemButton
                key={c.id}
                onClick={() => {
                  setConsigneeDetails({
                    id: c.id,
                    name: c.name || c.company || '',
                    address: c.address || '',
                    city: c.city || '',
                    state: c.state || '',
                    pincode: c.pincode || '',
                    gstin: c.gstin || '',
                  });
                  setBuyerOption('add-consignee');
                  setConsigneeSelectDialogOpen(false);
                }}
              >
                <ListItemText
                  primary={c.name || c.company}
                  secondary={`${c.city || ''} ${c.city && c.gstin ? '-' : ''} ${c.gstin || ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Dialog>

      {/* Add Buyer Dialog */}
      <Dialog
        open={addBuyerDialogOpen}
        onClose={() => setAddBuyerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Add New Buyer
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField fullWidth label="Name" size="small" value={buyerForm.name} onChange={(e)=>setBuyerForm(f=>({...f,name:e.target.value}))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Company" size="small" value={buyerForm.company} onChange={(e)=>setBuyerForm(f=>({...f,company:e.target.value}))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" size="small" multiline rows={2} value={buyerForm.address} onChange={(e)=>setBuyerForm(f=>({...f,address:e.target.value}))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="City" size="small" value={buyerForm.city} onChange={(e)=>setBuyerForm(f=>({...f,city:e.target.value}))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="State" size="small" value={buyerForm.state} onChange={(e)=>setBuyerForm(f=>({...f,state:e.target.value}))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Pincode" size="small" value={buyerForm.pincode} onChange={(e)=>setBuyerForm(f=>({...f,pincode:e.target.value}))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="GSTIN" size="small" value={buyerForm.gstin} onChange={(e)=>setBuyerForm(f=>({...f,gstin:e.target.value}))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Contact No" size="small" value={buyerForm.contact_no} onChange={(e)=>setBuyerForm(f=>({...f,contact_no:e.target.value}))} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" size="small" onClick={()=>setAddBuyerDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="primary" size="small" onClick={async ()=>{
              try {
                if (!buyerForm.name.trim()) return;
                const payload = { ...buyerForm, is_customer: true };
                const res = await createParty(payload);
                // Refresh list and set selected
                const list = await fetchParties('customer');
                setCustomers(list.data || []);
                const created = list.data?.find(p=>p.id === res.id) || null;
                if (created) setSelectedBuyer(created);
                setAddBuyerDialogOpen(false);
              } catch(err){
                let msg = 'Failed to create buyer';
                if (err?.response?.data?.error) {
                  msg = err.response.data.error;
                } else if (err?.message) {
                  msg = err.message;
                }
                window.alert(msg);
              }
            }}>Save</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Consignee Address Dialog */}
      <Dialog
        open={consigneeDialogOpen}
        onClose={() => {
          setConsigneeDialogOpen(false);
          setBuyerOption('show-consignee');
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Add Consignee Address
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Company/Person Name"
                size="small"
                variant="outlined"
                value={consigneeForm.name}
                onChange={(e)=>setConsigneeForm(f=>({...f,name:e.target.value}))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                size="small"
                variant="outlined"
                multiline
                rows={2}
                value={consigneeForm.address}
                onChange={(e)=>setConsigneeForm(f=>({...f,address:e.target.value}))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="City"
                size="small"
                variant="outlined"
                value={consigneeForm.city}
                onChange={(e)=>setConsigneeForm(f=>({...f,city:e.target.value}))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="State"
                size="small"
                variant="outlined"
                value={consigneeForm.state}
                onChange={(e)=>setConsigneeForm(f=>({...f,state:e.target.value}))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Pincode"
                size="small"
                variant="outlined"
                value={consigneeForm.pincode}
                onChange={(e)=>setConsigneeForm(f=>({...f,pincode:e.target.value}))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="GSTIN (Optional)"
                size="small"
                variant="outlined"
                value={consigneeForm.gstin}
                onChange={(e)=>setConsigneeForm(f=>({...f,gstin:e.target.value}))}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setConsigneeDialogOpen(false);
                setBuyerOption('show-consignee');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={async () => {
                try {
                  const payload = {
                    name: consigneeForm.name || '',
                    company: null,
                    address: consigneeForm.address || null,
                    city: consigneeForm.city || null,
                    state: consigneeForm.state || null,
                    pincode: consigneeForm.pincode || null,
                    gstin: consigneeForm.gstin || null,
                    contact_no: null,
                  };
                  const created = await createConsignee(payload);
                  const id = created?.id || null;
                  setConsigneeDetails({ id, ...payload });
                } catch (e) {
                  console.error('create consignee (different address) error', e);
                  // If creation fails, still keep local details to avoid user data loss
                  setConsigneeDetails(consigneeForm);
                } finally {
                  setConsigneeDialogOpen(false);
                  setBuyerOption('add-consignee');
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Select Transporter Dialog */}
      <Dialog
        open={selectTransporterDialogOpen}
        onClose={() => setSelectTransporterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Select Transporter
          </Typography>
          <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {savedTransporters.length > 0 ? (
              <List>
                {savedTransporters.map((transporter, idx) => (
                  <ListItemButton
                    key={idx}
                    onClick={() => {
                      handleSelectTransporter(transporter);
                      setSelectTransporterDialogOpen(false);
                    }}
                    sx={{ mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {transporter.transporter_name}, {transporter.transporter_contact_no}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {transporter.transporter_address}, {transporter.transporter_city}, {transporter.transporter_state} {transporter.transporter_pincode}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 3 }}>
                No saved transporters found
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectTransporterDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setTransporterDialogOpen(true)}
              startIcon={<PersonAddIcon />}
            >
              Add New
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Transporter Details Dialog */}
      <Dialog
        open={transporterDialogOpen}
        onClose={() => setTransporterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Add Transporter Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Transporter Name"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_name}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                size="small"
                variant="outlined"
                multiline
                rows={2}
                value={transportDetails.transporter_address}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="City"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_city}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="State"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_state}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Pincode"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_pincode}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_pincode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="GST Number"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_gstin}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_gstin: e.target.value }))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Contact Number"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_contact_no}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_contact_no: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setTransporterDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setTransporterDialogOpen(false);
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
